/**
 * HealthProvidersList — /health/providers with specialty + location filter.
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { UserRound, MapPin, Loader2, Stethoscope } from "lucide-react";
import { http } from "@/lib/api";
import HealthCategoryNav from "@/components/HealthCategoryNav";

export default function HealthProvidersList() {
  const [params, setParams] = useSearchParams();
  const group     = params.get("group") || "";
  const specialty = params.get("specialty") || "";
  const country   = params.get("country") || "";
  const city      = params.get("city") || "";
  const q         = params.get("q") || "";
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [meta, setMeta]   = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { http.get("/health/meta").then(r => setMeta(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    setLoading(true);
    const p = {};
    if (group)     p.profession_group = group;
    if (specialty) p.specialty = specialty;
    if (country)   p.country = country;
    if (city)      p.city = city;
    if (q)         p.q = q;
    http.get("/health/providers", { params: p })
      .then(r => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
      .catch(() => { setItems([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [group, specialty, country, city, q]);

  const setFilter = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    if (k === "group") next.delete("specialty"); // reset specialty when group changes
    setParams(next);
  };

  const specialties = (meta?.provider_specialties?.[group] || []);

  return (
    <>
      <HealthCategoryNav />
      <div className="max-w-6xl mx-auto px-4 py-8" data-testid="providers-list">
        <div className="mb-6">
          <div className="overline mb-1">HEALTHCARE PROFESSIONALS</div>
          <h1 className="font-display text-4xl mb-1">Find a Doctor / Professional</h1>
          <p className="opacity-70">{total} listed</p>
        </div>

        <div className="wofy-card !p-3 mb-6 flex flex-wrap gap-2">
          <select value={group} onChange={e => setFilter("group", e.target.value)}
            className="text-sm px-2 py-1 rounded border" style={{ borderColor: "var(--wofy-border)" }}
            data-testid="filter-group">
            <option value="">All professions</option>
            {(meta?.profession_groups || []).map(g => <option key={g.slug} value={g.slug}>{g.label}</option>)}
          </select>
          {specialties.length > 0 && (
            <select value={specialty} onChange={e => setFilter("specialty", e.target.value)}
              className="text-sm px-2 py-1 rounded border" style={{ borderColor: "var(--wofy-border)" }}
              data-testid="filter-specialty">
              <option value="">All specialties</option>
              {specialties.map(s => <option key={s.slug} value={s.slug}>{s.label}</option>)}
            </select>
          )}
          <input value={country} onChange={e => setFilter("country", e.target.value)}
            placeholder="Country" className="text-sm px-2 py-1 rounded border w-32" style={{ borderColor: "var(--wofy-border)" }} />
          <input value={city} onChange={e => setFilter("city", e.target.value)}
            placeholder="City" className="text-sm px-2 py-1 rounded border w-32" style={{ borderColor: "var(--wofy-border)" }} />
          <input value={q} onChange={e => setFilter("q", e.target.value)}
            placeholder="Search…" className="text-sm px-2 py-1 rounded border flex-1 min-w-[8rem]"
            style={{ borderColor: "var(--wofy-border)" }} />
        </div>

        {loading ? (
          <div className="opacity-60 text-sm inline-flex items-center gap-1.5"><Loader2 className="w-4 h-4 animate-spin"/>Loading…</div>
        ) : items.length === 0 ? (
          <div className="wofy-card !p-8 text-center opacity-70">
            No providers match your filters. Encourage a local professional to <Link to="/health/providers/new" className="wofy-link">register</Link>.
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {items.map(p => (
              <Link key={p.id} to={`/health/providers/${p.slug}`}
                className="wofy-card !p-4 hover:shadow-md transition block"
                data-testid={`provider-card-${p.slug}`}>
                <div className="flex items-start gap-3">
                  {p.photo_url ? (
                    <img src={p.photo_url} alt="" className="w-12 h-12 rounded-full object-cover shrink-0"/>
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-black/5 grid place-items-center shrink-0"><UserRound className="w-5 h-5"/></div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{p.name}</div>
                    <div className="text-[10px] font-mono uppercase tracking-wider opacity-60 mt-0.5">
                      {p.profession_group}{p.specialty ? ` · ${p.specialty}` : ""}
                    </div>
                    {(p.city || p.country) && (
                      <div className="text-xs opacity-70 flex items-center gap-1 mt-1">
                        <MapPin className="w-3 h-3"/>{[p.city, p.state, p.country].filter(Boolean).join(", ")}
                      </div>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
